import { ComponentProps, ReactNode, useState } from 'react';
import { CdaStructuredTextRecord } from 'react-datocms/structured-text';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import InlineRichText from 'components/RichContent/InlineRichText';

import styles from './ComparisonEntry.module.css';

interface ComparisonEntryRecord extends CdaStructuredTextRecord {
  category: string;
  ourOfferTitle: string;
  ourOfferDescription: string;
  competitorOfferTitle: string;
  competitorOfferDescription: string;
  defaultOpen?: boolean;
}

interface ComparisonHeaderProps extends ComponentProps<'hgroup'> {
  id: string;
  children: ReactNode;
  theme?: 'success' | 'danger';
}

function ComparisonHeader({ id, children, className, theme = 'success', ...props }: ComparisonHeaderProps) {
  const Icon = theme === 'success' ? CheckCircleIcon : XCircleIcon;
  return (
    <hgroup
      className={clsx(styles.header, className)}
      tabIndex={0}
      {...props}
    >
      <Icon className={clsx(styles.icon, styles[theme])} />
      <h4
        className={styles.title}
        aria-controls={id}
      >
        {children}
      </h4>
      <ChevronDownIcon className={styles.chevron} />
    </hgroup>
  );
}

export default function ComparisonEntry(
  {
    id,
    category,
    ourOfferTitle,
    ourOfferDescription,
    competitorOfferTitle,
    competitorOfferDescription,
  }: ComparisonEntryRecord,
  index: number = 0,
) {
  const [isOpen, setIsOpen] = useState(index === 0);

  const toggleOpen = () => setIsOpen((isOpen) => !isOpen);

  return (
    <div
      className={styles.container}
      aria-expanded={isOpen}
      id={id}
      aria-labelledby={`${id}-category`}
    >
      <div className={clsx(styles.card, styles.our, isOpen && styles.open)}>
        <ComparisonHeader
          id={id}
          onClick={toggleOpen}
        >
          {ourOfferTitle}
        </ComparisonHeader>
        <InlineRichText
          as="p"
          className={styles.description}
        >
          {ourOfferDescription}
        </InlineRichText>
      </div>
      <span
        id={`${id}-category`}
        className={styles.category}
      >
        {category}
      </span>
      <div className={clsx(styles.card, styles.competitor, isOpen && styles.open)}>
        <ComparisonHeader
          id={id}
          onClick={toggleOpen}
          theme="danger"
        >
          {competitorOfferTitle}
        </ComparisonHeader>
        <InlineRichText
          as="p"
          className={styles.description}
        >
          {competitorOfferDescription}
        </InlineRichText>
      </div>
    </div>
  );
}
